<template>
  <div>
    <Modal
      v-model="showModal"
      class="modal"
      fullscreen
      footer-hide
      @on-visible-change="closePreview"
    >
      <div
        slot="header"
        class="modal-title"
      >
        <p class="title">
          {{ documentTitle }}
          <!-- <template v-show="documentType === 'google' && editable && isOwner">
            <Input v-model="documentTitle" placeholder="Your spreadsheet title" />
          </template> -->
        </p>
        <div class="buttons">
          <Button
            class="modal-button"
            @click="$feedback(owners, documentTitle, 'dashboard', $route.path)"
          >
            <Tooltip
              :content="owners"
              placement="bottom"
            >
              <i class="fas fa-envelope fa-lg" />
              <span v-if="!isMobile">Email Owner</span>
            </Tooltip>
          </Button>
          <Button
            v-show="documentType === 'google' && editable && isOwner"
            :class="{ mobile: isMobile }"
            class="modal-button"
            @click="openDocument(url)"
          >
            <i class="fab fa-google-drive fa-lg" />
            <span v-if="!isMobile">Open in Google Sheets</span>
          </Button>
          <Button
            v-show="documentType === 'google' && copyable && !isOwner"
            :class="{ mobile: isMobile }"
            class="modal-button"
            @click="copyDocument(url)"
          >
            <i class="fab fa-google-drive fa-lg" />
            <span v-if="!isMobile">Make a copy</span>
          </Button>
          <Button
            v-show="documentType === 'google' && downloadable && !isOwner"
            :class="{ mobile: isMobile }"
            class="modal-button"
            @click="downloadDocument(url)"
          >
            <i class="fas fa-download fa-lg" />
            <span v-if="!isMobile">Download</span>
          </Button>
          <Button
            v-show="recommendedDashboards.length && !isMobile"
            class="modal-button"
            @click="showRecommendedDashboards = true"
          >
            <i class="fas fa-compass" />
            <span>Explore More</span>
          </Button>
          <Dropdown
            v-if="dashboardDocs.length"
            class="modal-button dropdown"
          >
            <i class="fas fa-file-alt" />
            <a
              href="javascript:void(0)"
              style="color: var(--color-black);"
            >
              Docs
              <Icon type="ios-arrow-down" />
            </a>
            <DropdownMenu slot="list">
              <DropdownItem
                v-for="doc in dashboardDocs"
                :key="doc.name"
              >
                <a
                  :href="doc.url"
                  target="_blank"
                  style="color: var(--color-black);"
                >
                  {{ doc.name }}
                </a>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      <div
        v-if="documentType === 'new-relic-group'"
        class="new-relic"
      >
        <Row class="header-row">
          <i-col span="18">
            <h2>
              <Icon
                v-if="iconType(dashboard.icon) === 'iview'"
                :type="dashboard.icon"
                :size="28"
              />
              <i
                v-else
                :class="dashboard.icon"
                class="dashboard-icon"
              />
              {{ dashboard.title }}
            </h2>
            <p>
              Created by
              <span
                class="contact"
                @click="$feedback(dashboard.owners, dashboard.title, 'dashboard', $route.path)"
              >
                {{ dashboard.owners }}
              </span>
            </p>
          </i-col>
          <i-col
            span="6"
            class="right"
          >
            <Select
              v-model="widgetTime"
              size="large"
              style="width:200px"
              @on-change="updateWidgets"
            >
              <Option
                v-for="time in timesList"
                :key="time.value"
                :label="time.label"
                :value="time.value"
              />
            </Select>
          </i-col>
        </Row>
        <Row
          v-if="dashboard.widgets"
          class="row"
        >
          <i-col
            v-for="(widget, index) in widgetsPerPage"
            :key="index"
            class="column"
          >
            <h3>
              {{ widget.title }}
              <Tooltip
                v-if="!isMobile"
                content="Open Widget in a new Window"
                placement="bottom"
                @click.native="openDocument(widget.url)"
              >
                <i class="fas fa-link action-icon" />
              </Tooltip>
              <i
                v-else
                class="fas fa-link action-icon"
                @click="openDocument(widget.url)"
              />
            </h3>
            <p>{{ widget.description }}</p>
            <iframe
              v-cloak
              :ref="`iframe-${index}`"
              scrolling="no"
              :src="widget.url"
            />
          </i-col>
        </Row>
        <Page
          v-if="dashboard.widgets && dashboard.widgets.length > pageSize"
          :current="currentPage + 1"
          :page-size="pageSize"
          :total="dashboard.widgets.length"
          show-elevator
          @on-change="onPageChange"
        />
      </div>
      <template v-else>
        <div class="iframe-container">
          <iframe
            :src="urlPreview"
            allowfullscreen
          />
        </div>
      </template>
    </Modal>
    <Modal
      v-model="confirmCopy"
      class-name="vertical-center-modal"
      @on-ok="displayCopyWindow"
    >
      <p
        slot="header"
        style="color: var(--color-blue);"
      >
        <Icon
          type="ios-information-circle-outline"
          size="24"
          style="margin-right: 8px;"
        />
        <span>Make a copy</span>
      </p>
      <p style="text-align: justify">
        Please be advised that your copy will not sync up with the original one. If you want to proceed, please click on <strong>OK</strong> button and then, click on <strong>Make a copy</strong> button from <strong>Google's</strong> page.
      </p>
    </Modal>
    <DashboardModal
      v-model="showRecommendedDashboards"
      :dashboards="recommendedDashboards"
      title="Recommended Dashboards"
    />
  </div>
</template>

<script>
import DashboardModal from '@/components/helper-components/DashboardModal'

import isMobile from '@/helpers/isMobile'
import _ from 'lodash'
import axios from 'axios'

export default {
  components: {
    DashboardModal
  },

  props: {
    showDocument: Boolean,
    documentTitle: String,
    documentType: String,
    owners: String,
    url: String,
    urlPreview: String,
    editable: Boolean,
    copyable: Boolean,
    downloadable: Boolean,
    isOwner: Boolean,
    dashboard: {
      type: Object,
      default: () => {}
    }
  },

  data () {
    return {
      isMobile: isMobile(),
      urlCopy: '',
      confirmCopy: false,
      widgetTime: 'one_week',
      pageSize: 24,
      currentPage: 0,
      timesList: [
        {
          value: 'one_day',
          label: '1 Day'
        },
        {
          value: 'three_days',
          label: '3 Days'
        },
        {
          value: 'one_week',
          label: '1 Week'
        },
        {
          value: 'ten_days',
          label: '10 Days'
        }
      ],
      recommendedDashboards: [],
      showRecommendedDashboards: false
    }
  },

  computed: {
    widgetsPerPage () {
      const startingIndex = this.currentPage * this.pageSize
      return this.dashboard.widgets.slice(startingIndex, startingIndex + this.pageSize)
    },

    showModal: {
      get () {
        return this.showDocument
      },
      set (value) {
        return value
      }
    },

    dashboardDocs () {
      return JSON.parse(this.dashboard.docs || '[]')
    }
  },

  watch: {
    dashboard: {
      handler () {
        if (!_.isEmpty(this.dashboard)) {
          this.getRecommendedDashboards()
        }
      },
      deep: true
    }
  },

  mounted () {
    window.addEventListener('resize', this.checkMobile)
    if (!_.isEmpty(this.dashboard)) {
      this.getRecommendedDashboards()
    }
  },

  methods: {
    /**
     * Gets the recommended dashboard for the current dashboard displayed in
     * the document embedding
     */
    async getRecommendedDashboards () {
      try {
        const recommendedDashboards = (await axios.get(`${process.env.VUE_APP_API_URI}/dashboards/recommendation`, {
          params: {
            currentDashboardId: this.dashboard.id,
            tags: this.dashboard.categories,
            limit: 9
          }
        })).data

        this.recommendedDashboards = recommendedDashboards
      } catch (error) {
        console.error(`Error while fetching recommended dashboards: ${error.message}`)
      }
    },

    /**
     * Validates if the device is a mobile or not.
     * @param {Event} e
     */
    checkMobile (e) {
      this.isMobile = isMobile(e)
    },

    /**
     * Closes the document preview
     * @param {Boolean} isPreviewOpen
     */
    closePreview (isPreviewOpen) {
      this.$emit('closePreview', isPreviewOpen)
    },
    /**
     * Opens the document's link
     * @param {String} url
     */
    openDocument (url) {
      window.open(url, '_blank')
    },

    /**
     * Creates a copy from the current Google Dashboard into the user's Google Drive.
     * @param {String} url
     */
    copyDocument (url) {
      this.confirmCopy = true
      this.urlCopy = `${url}/copy`
    },

    /**
     * Opens a popup window letting the user know that the copy he/she will made into his/her Google Drive
     * won't be sync up with the original one.
     */
    displayCopyWindow () {
      window.open(this.urlCopy, '_blank')
      this.confirmCopy = false
    },

    /**
     * Allows users to download Google Dashboards.
     * @param {String} url
     */
    downloadDocument (url) {
      const format = url.includes('document') ? 'docx' : (url.includes('spreadsheets') ? 'xlsx' : 'pdf')
      this.$log('dashboard_download', this.documentTitle, { format, type: this.documentType })
      window.open(`${url}/export?format=${format}`, '_blank')
    },

    /**
     * Returns the icon type: ivew icon / Font-Aswesome icon
     * @param {String} icon
     * @return {String}
     */
    iconType (icon) {
      if (!icon) {
        return
      }
      return icon.split(' ').length < 2 ? 'iview' : 'fa'
    },

    /**
     * Updates the New Relic widgets if a new period is selected from the dropbox.
     */
    updateWidgets () {
      this.dashboard.widgets.forEach(widget => {
        const url = widget.url.split('?').shift()
        this.$set(widget, 'url', `${url}?time=${this.widgetTime}`)
      })
    },

    /**
     * Changes the current page to a new desired page.
     * @param {Number} pageNum
     */
    onPageChange (pageNum) {
      this.currentPage = pageNum - 1
    }
  }
}
</script>

<style scoped>
  .vertical-center-modal {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal >>> .ivu-modal-close {
    height: 40px;
    line-height: 40px;
    top: 6px;
    right: 16px;
  }
  .modal >>> .ivu-modal-close .ivu-icon {
    color: var(--color-white);
    font-weight: 700;
  }
  .modal >>> .ivu-modal-header {
    background-color: var(--color-green);
  }
  .modal .modal-title {
    margin: -14px -16px;
    padding: 0 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: 56px;
    line-height: 56px;
    color: var(--color-white);
  }
  .modal .modal-title .title {
    width: calc(100% - 88px);
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--color-white);
  }
  .modal .modal-title .buttons {
    width: 88px;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }
  .modal .modal-title .buttons .modal-button {
    width: 16px;
    height: 24px;
    margin-left: 8px;
    padding: 0;
    border-radius: 50%;
    font-weight: bold;
    color: var(--color-white);
    background-color: transparent;
    border-color: transparent;
  }

  .modal .modal-title .buttons .modal-button i {
    margin: 0;
  }

  .modal .modal-title .buttons .modal-button span {
    display: none;
  }

  .iframe-container {
    overflow: hidden;
    display: contents;
  }

  .iframe-container iframe {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 6px;
    left: 0;
    border: 0;
    overflow: hidden;
  }

  .new-relic .header-row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .new-relic .header-row .right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .new-relic .dashboard-icon {
    font-size: 18px;
  }

  .new-relic .action-icon {
    margin-left: 8px;
    color: var(--color-gray-button);
    cursor: pointer;
  }

  .action-icon:hover {
    color: var(--color-black);
  }

  .new-relic p {
    font-size: 12px;
  }

  .new-relic .contact {
    color: var(--color-anchor);
    font-weight: bold;
    cursor: pointer;
  }

  .new-relic .row {
    margin-top: 16px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .new-relic .column {
    width: 100%;
    height: auto;
    margin-bottom: 16px;
    padding: 8px;
    background-color: var(--color-white);
  }

  .new-relic .column p {
    color: var(--color-mid-gray);
    font-weight: bold;
  }

  .new-relic .column iframe {
    width: 100%;
    height: 240px;
    overflow: auto;
    border: none;
    position: relative;
    bottom: 0;
    left: 0;
  }

  @media screen and (min-width: 768px) {
    .new-relic .column {
      width: calc(50% - 16px);
      margin: 0 8px 16px;
    }
  }

  @media screen and (min-width: 1024px) {
    .modal .modal-title .title {
      width: unset;
    }
    .modal .modal-title .buttons {
      width: auto;
    }
    .modal .modal-title .buttons .modal-button {
      width: unset;
      margin-left: 24px;
      padding-left: 16px;
      padding-right: 16px;
      border-radius: 4px;
      color: var(--color-black);
      background-color: var(--color-green-hover);
      border-color: var(--color-green-hover);
    }
    .modal .modal-title .buttons .modal-button i {
      margin-right: 8px;
    }
    .modal .modal-title .buttons .modal-button span {
      display: inline-block;
    }

    .new-relic .go-back {
      color: var(--color-anchor-light);
    }

    .new-relic .go-back:hover {
      color: var(--color-anchor);
    }

    .new-relic .column {
      width: calc(100% / 3) - 16px;
    }
  }

  @media screen and (min-width: 1440px) {
    .new-relic .column {
      width: calc(25% - 16px);
    }
  }

  .dropdown {
    display: flex;
    align-items: center;
  }
</style>
